import { create } from "zustand";
import { persist } from "zustand/middleware";

interface MyTx {
  hash: string;
  label?: string;
}

const MAX_CLIPBOARD = 5;

interface MyTxsState {
  txs: MyTx[];
  clipboards: Record<string, string[]>; // networkId → hashes
  add: (hash: string, label?: string) => void;
  remove: (hash: string) => void;
  isTracked: (hash: string) => boolean;
  getLabel: (hash: string) => string | undefined;
  addToClipboard: (networkId: string, hash: string) => void;
  removeFromClipboard: (networkId: string, hash: string) => void;
  clearClipboard: (networkId: string) => void;
  getClipboard: (networkId: string) => string[];
  isInClipboard: (networkId: string, hash: string) => boolean;
}

export const useMyTxs = create<MyTxsState>()(
  persist(
    (set, get) => ({
      txs: [],
      clipboards: {},
      add: (hash, label) =>
        set((s) => ({
          txs: s.txs.some((t) => t.hash === hash)
            ? s.txs
            : [...s.txs, { hash, label }],
        })),
      remove: (hash) =>
        set((s) => ({ txs: s.txs.filter((t) => t.hash !== hash) })),
      isTracked: (hash) => get().txs.some((t) => t.hash === hash),
      getLabel: (hash) => get().txs.find((t) => t.hash === hash)?.label,
      addToClipboard: (networkId, hash) =>
        set((s) => {
          const current = s.clipboards[networkId] ?? [];
          if (current.includes(hash) || current.length >= MAX_CLIPBOARD) return s;
          return { clipboards: { ...s.clipboards, [networkId]: [...current, hash] } };
        }),
      removeFromClipboard: (networkId, hash) =>
        set((s) => ({
          clipboards: {
            ...s.clipboards,
            [networkId]: (s.clipboards[networkId] ?? []).filter((h) => h !== hash),
          },
        })),
      clearClipboard: (networkId) =>
        set((s) => ({ clipboards: { ...s.clipboards, [networkId]: [] } })),
      getClipboard: (networkId) => get().clipboards[networkId] ?? [],
      isInClipboard: (networkId, hash) => (get().clipboards[networkId] ?? []).includes(hash),
    }),
    { name: "clustec-my-txs" }
  )
);
